// packages/ui-kit/src/primitives/FormField.tsx
// C3F v18 — reference: design/system/components/forms/FormField.{jsx,prompt.md}
// Label / hint / error wrapper. The error REPLACES the hint, never sits
// beside it.
import { type ReactNode, type ReactElement, isValidElement, cloneElement, useId } from "react";

interface Props {
  label: string;
  /** Help line under the field (DS name). */
  hint?: string;
  /** Legacy alias of `hint` — existing call sites keep working. */
  helper?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}

export function FormField({ label, hint, helper, error, required, children }: Props) {
  const generatedId = useId();
  // Associate the <label> with its control so screen readers announce the
  // field and `getByLabelText` finds it. Reuse the child's own `id` when it
  // set one; otherwise inject a generated id into the (single) child element.
  const child = isValidElement(children) ? (children as ReactElement<{ id?: string }>) : null;
  const controlId = child?.props.id ?? generatedId;
  const control =
    child && child.props.id === undefined ? cloneElement(child, { id: controlId }) : children;
  const help = hint ?? helper;

  return (
    <div className="grid gap-[7px]">
      <label htmlFor={controlId} className="text-sm font-semibold tracking-body text-fg-default">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>
      {control}
      {error ? (
        <p className="text-caption text-danger">{error}</p>
      ) : help ? (
        <p className="text-caption text-fg-muted">{help}</p>
      ) : null}
    </div>
  );
}
